import React from "react";
import "./OutstandingPeople.css";

/**
 * AL-455 — how many people are still holding a case up.
 *
 * Two renderings, deliberately no table column. The records table is already six
 * columns and tight; a seventh made it worse, and putting the count in a column
 * AND a link in the Action cell stated the same fact twice on one row — swapping
 * one redundancy for another.
 *
 * So the count lives in the existing Status cell, on a second line under the
 * status badge. It costs a row of height rather than a column of width, and sits
 * directly beneath the status it qualifies.
 *
 *   OutstandingCount   records table, under the Status badge. Clickable.
 *   OutstandingText    dashboard card, a plain sentence under the reference.
 *
 * ── The distinction everything here turns on ──
 *
 *   counts absent    "we do not know" — the backend omits these rather than
 *                    sending zero when the rollup could not be computed
 *   outstanding = 0  "everyone is accounted for"
 *   outstanding > 0  "N people can be chased"
 *
 * Nothing is rendered for either of the first two. That is safe here in a way it
 * would not be in a dedicated column: a blank column cell reads as zero, but a
 * Status cell that simply says "Completed" claims nothing about people either
 * way. The absent/zero distinction still matters and is still not collapsed —
 * neither produces output, and neither produces a false all-clear.
 *
 * `outstandingPeople` counts only people the C1 can act on — waitingOn INVITEE
 * or C1 — not everyone incomplete. A larger number would send them after people
 * we are already processing.
 */

interface Counts {
	totalPeople?: number;
	outstandingPeople?: number;
}

/** Null unless someone is genuinely outstanding. Absent is never treated as zero. */
function outstandingOf({ totalPeople, outstandingPeople }: Counts): {
	outstanding: number;
	total: number;
} | null {
	if (typeof totalPeople !== "number" || typeof outstandingPeople !== "number") return null;
	if (totalPeople <= 0) return null;

	const outstanding = Math.max(0, Math.min(outstandingPeople, totalPeople));
	return outstanding > 0 ? { outstanding, total: totalPeople } : null;
}

/** One phrasing for both surfaces, so they cannot disagree. */
const sentence = (outstanding: number, total: number) =>
	`Waiting on ${outstanding} of ${total} ${outstanding === 1 ? "person" : "people"}`;

/* ───────────────────────── records table ───────────────────────── */

/**
 * Its own line beneath the status badge:
 *
 *   In Progress
 *   2 of 3 outstanding
 *
 * It was briefly appended to the badge's own line as "In Progress · 2 of 3",
 * which fitted but cost the word that gives the number its meaning — a bare
 * "2 of 3" beside a status reads as a step counter, not as people still to
 * complete. On its own line there is room to say "outstanding", and the count
 * stops competing with the status label for the same line of sight.
 *
 * Still a quiet control rather than a button: it sits in a cell whose job is to
 * report state, and the Action cell already owns the row's real affordance. The
 * fuller sentence is its accessible name.
 */
export const OutstandingCount: React.FC<Counts & { recordId: string; onClick: () => void }> = ({
	recordId,
	onClick,
	...counts
}) => {
	const r = outstandingOf(counts);
	if (!r) return null;

	return (
		<button
			type="button"
			className="op-count"
			onClick={onClick}
			// Signals a dialog opens, so the focus move is expected rather than
			// disorienting.
			aria-haspopup="dialog"
			aria-label={`${sentence(r.outstanding, r.total)}. Open for detail.`}
			data-testid={`btn-outstanding-${recordId}`}
		>
			<span className="op-num">
				{r.outstanding} of {r.total}
			</span>{" "}
			outstanding
		</button>
	);
};

/* ───────────────────────── dashboard card ───────────────────────── */

/**
 * Plain text for the dashboard card, which has room for a sentence and no status
 * cell to append to. Rendered only when someone is outstanding — a card is a
 * summary, and "all accounted for" is not worth a line there.
 */
export const OutstandingText: React.FC<Counts & { recordId: string }> = ({
	recordId,
	...counts
}) => {
	const r = outstandingOf(counts);
	if (!r) return null;

	return (
		<p className="op-text" data-testid={`record-outstanding-${recordId}`}>
			{sentence(r.outstanding, r.total)}
		</p>
	);
};
